import { getRealmFromMarketplace, REALMS } from "./marketplaceUtils";
import { Stage } from "../lusca-search-api/generated-src";
import { PaymentMethod } from "../types/documents";
import {
  AccountingEventsGroup,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../types";

export function getDigitalOrderId(item?: {
  accountingEventsGroup?: AccountingEventsGroup;
  digitalBusinessDocument?: DigitalBusinessDocument;
  digitalCostDocument?: DigitalCostDocument;
  digitalPaymentDocument?: DigitalPaymentDocument;
}): string {
  if (item?.accountingEventsGroup?.accountingEvents) {
    const firstEvent = Object.values(
      item.accountingEventsGroup.accountingEvents
    )[0];
    if (firstEvent?.orderId) return firstEvent.orderId;
  }
  if (item?.digitalBusinessDocument) {
    return item.digitalBusinessDocument.body?.header?.id || "";
  }
  if (item?.digitalCostDocument) {
    return item.digitalCostDocument.body?.header?.id || "";
  }
  if (item?.digitalPaymentDocument) {
    return item.digitalPaymentDocument.body?.header?.id || "";
  }
  return "";
}

// Extracts the best available Payment Contract Revision ID from a payment method.
// Checks complete, failed, and authorizationState in priority order, falling back to methodId.
export const extractPcrId = (method: PaymentMethod, methodId: string): string =>
  method.obligation.state.complete?.transactionId ??
  method.obligation.state.failed?.transactionId ??
  method.obligation.authorizationState?.complete?.transactionId ??
  methodId;

// There are two payment ID formats:
//   - amzn1.pplan: transactionId version suffix uses "-" (e.g. "xxx-1"),
//     but link expects "_" (e.g. "xxx_1")
//   - amzn1.pc.pma: transactionId already uses "_" for the version suffix,
//     no conversion needed for link
export const getPaymentContractRevisionId = (pcrId: string): string => {
  if (pcrId.startsWith("amzn1.pplan.")) {
    const lastDash = pcrId.lastIndexOf("-");
    const suffix = pcrId.substring(lastDash + 1);
    if (/^\d+$/.test(suffix)) {
      return `${pcrId.substring(0, lastDash)}_${suffix}`;
    }
  }
  return pcrId;
};

export const createPaystationLink = (marketplaceId: string, pcrId: string) => {
  const realm = getRealmFromMarketplace(marketplaceId);
  const normalizedPcrId = getPaymentContractRevisionId(pcrId);
  return `https://paystation-console-${realm}.aka.amazon.com/pcds?api=getPCRById&pcrId=${encodeURIComponent(
    normalizedPcrId
  )}`;
};

const getFlashUIBaseUrl = (marketplaceId: string, selectedStage: Stage) => {
  let realm = getRealmFromMarketplace(marketplaceId);

  if (selectedStage === Stage.Beta) {
    realm = REALMS.NA; // Flare beta redirects all traffic to NA
  }

  const stage =
    selectedStage === Stage.Prod ? "" : `-${selectedStage.toLowerCase()}`;
  const alias = selectedStage === Stage.Beta ? "integ" : "aka";
  const domain = selectedStage === Stage.Prod ? "" : "&domain=uat";
  const baseUrl = `https://flashui${stage}-${realm}.${alias}.amazon.com/search`;

  return { realm, baseUrl, domain };
};

export const createFlashUILink = (
  marketplaceId: string,
  orderId: string,
  selectedStage: Stage
) => {
  const { baseUrl } = getFlashUIBaseUrl(marketplaceId, selectedStage);
  return `${baseUrl}/transaction?transactionId=${encodeURIComponent(orderId)}`;
};

export const createFlashUIMatcherLink = (
  marketplaceId: string,
  rcslEntrySourceId: string,
  selectedStage: Stage,
  accountNumber: string = "23110"
) => {
  const { baseUrl, realm, domain } = getFlashUIBaseUrl(
    marketplaceId,
    selectedStage
  );
  return `${baseUrl}/matcher?matchKey=${encodeURIComponent(
    rcslEntrySourceId
  )}&accountNumber=${accountNumber}&realm=${realm}Amazon${domain}`;
};

export const createDigiConLink = (marketplaceId: string, orderId: string) => {
  if (orderId.startsWith("d")) {
    return `https://digicon.integ.amazon.com/order-dumper?orderID=${orderId}`;
  }

  const realm = getRealmFromMarketplace(marketplaceId);
  if (realm !== REALMS.NA) {
    const realmSuffix = `${realm.toUpperCase()}Amazon`;
    return `https://digicon-${realm}.amazon.com/order-dumper?orderID=${orderId}&realm=${realmSuffix}`;
  }
  return `https://digicon.amazon.com/order-dumper?orderID=${orderId}`;
};

export const createRaceLink = (
  marketplaceId: string,
  deferralId: string,
  selectedStage: Stage
) => {
  const namespace = selectedStage === Stage.Prod ? "prod" : "uat";
  if (selectedStage === Stage.Beta) {
    return `https://race-website.integ.amazon.com:13743/deferral_search/index?deferral-id=${encodeURIComponent(
      deferralId
    )}&namespace=test`;
  }
  const realm = getRealmFromMarketplace(marketplaceId);
  const endpoint = realm === REALMS.EU ? "eu-south-2" : realm;
  return `https://race-website-${endpoint}.aka.amazon.com/deferral_search/index?deferral-id=${encodeURIComponent(
    deferralId
  )}&namespace=${namespace}`;
};

export const createSubsLink = (
  stage: Stage,
  marketplaceId: string,
  subscriptionId: string
) => {
  if (!subscriptionId) return null;
  const realm = getRealmFromMarketplace(marketplaceId);
  if (stage === Stage.Beta)
    return `https://subscriptions-${realm}-beta.integ.amazon.com/#/subscription/${subscriptionId}`;
  return `https://subscriptions-${realm}.amazon.com/#/subscription/${subscriptionId}`;
};

export const createTaxLink = (
  stage: Stage,
  marketplaceId: string,
  taxId: string
): string => {
  const realm = getRealmFromMarketplace(marketplaceId).toUpperCase();
  const subdomain = stage === Stage.Beta ? "beta." : "";
  return `https://${subdomain}console.tax.amazon.dev/tarviewer/search/DigitalTransactionId/${taxId}/${realm}ECP/default`;
};

const getHerdBaseUrl = (
  marketplaceId: string,
  stage: Stage
): { baseUrl: string; client: string } => {
  const CLIENTS = {
    PROD: "gfs-calvin-herd",
    UAT: "gfs-calvin-herd-uat",
    BETA: "gfs-calvin-herd",
  };

  const realm = getRealmFromMarketplace(marketplaceId);

  const regionMap: Record<string, string> = {
    [REALMS.NA]: "us-east-1",
    [REALMS.EU]: "eu-west-1",
    [REALMS.FE]: "us-west-2",
  };

  if (stage === Stage.Beta) {
    return {
      baseUrl: `https://beta.us-west-2.studio.orca.amazon.dev`,
      client: CLIENTS.BETA,
    };
  }

  const region = regionMap[realm];

  const client = stage === Stage.Prod ? CLIENTS.PROD : CLIENTS.UAT;

  return {
    baseUrl: `https://${region}.studio.orca.amazon.dev`,
    client,
  };
};

export const createHerdObjectId = (cbe: {
  businessNamespace: string;
  idempotenceId: string;
  idempotenceVersion: number;
}): string =>
  `${cbe.businessNamespace}.${cbe.idempotenceId}.${cbe.idempotenceVersion}`;

export const createHerdLink = (
  cbe: {
    businessNamespace: string;
    idempotenceId: string;
    idempotenceVersion: number;
  },
  marketplaceId: string,
  stage: Stage
): string => {
  const { baseUrl, client } = getHerdBaseUrl(marketplaceId, stage);
  const executionPath = createHerdObjectId(cbe);

  return `${baseUrl}/#/clients/${client}/executions/${executionPath}`;
};

export const createRoyaltyTraceLink = (
  orderId: string,
  stage: Stage
): string => {
  const subdomain =
    !stage || stage === Stage.Prod ? "" : `${stage.toLowerCase()}.`;
  return `https://${subdomain}royalty-trace.prime-video.amazon.dev/royalty-search?id=${encodeURIComponent(
    orderId
  )}&idType=order_id`;
};

export const createFRKConsoleLink = (
  orderId: string,
  stage: Stage,
  version?: number
): string => {
  const subdomain = stage === Stage.Beta ? Stage.Beta : Stage.Prod;

  const versionParam = version ? `&version=${version}` : "";

  return `https://${subdomain}.digital-central.da.amazon.dev/features/frk/transaction-record?type=digital.ta%3Arecord%3A%3Aalacarte&id=${encodeURIComponent(
    orderId
  )}&stage=${stage.toLowerCase()}&region=NA${versionParam}`;
};

export const generatePromotionLink = (
  promotionId: string,
  marketplaceId: string
): string => {
  const realm = getRealmFromMarketplace(marketplaceId);
  return `https://pithub-${realm}.amazon.com/promotion/info/${promotionId}`;
};

export const createPaymentContractLink = (
  marketplaceId: string,
  paymentContractId: string
): string => {
  const realm = getRealmFromMarketplace(marketplaceId);
  return `https://paystation-console-${realm}.aka.amazon.com/pces?api=byPaymentContractId&paymentContractId=${encodeURIComponent(
    paymentContractId
  )}`;
};

export const createPaymentProfileLink = (marketplaceId: string): string => {
  const realm = getRealmFromMarketplace(marketplaceId);
  return `https://payment-contract-ui-${realm}.amazon.com/business_profiles`;
};
